import { Link, useLocation } from 'react-router-dom'
import {
  LayoutDashboard, Mail, Bot, CheckSquare, PhoneCall, Calendar, Users, Building2,
  Star, FileText, TrendingUp, Inbox, Lightbulb, Clapperboard, DollarSign,
  BarChart3, Search, Flame, HardDrive, Settings, MessageSquare, Wrench,
} from 'lucide-react'
import { useI18n } from '../../contexts/I18nContext'
import { useTheme } from '../../contexts/ThemeContext'
import { useMessagesUnread } from '../../contexts/MessagesUnreadContext'
import { useJanetQueueCount } from '../../hooks/useJanetQueue'
import { BLOCK_NAV, BLOCKS } from '../../config/themes'

// Per-tab icons, keyed by base route — the same glyphs the old sidebar used.
const TAB_ICONS = {
  '/': LayoutDashboard, '/inbox': Mail, '/janet/queue': Bot, '/messages': MessageSquare, '/tasks': CheckSquare, '/comms': PhoneCall, '/calendar': Calendar,
  '/contacts': Users, '/companies': Building2,
  '/clients': Star, '/properties': FileText, '/deals': TrendingUp, '/submissions': Inbox, '/opportunities': Lightbulb,
  '/projects': Clapperboard,
  '/finance': DollarSign, '/reports': BarChart3,
  '/research': Search, '/audience': Flame,
  '/engineering': Wrench,
  '/files': HardDrive, '/settings': Settings,
}

// The top tab bar for the active Block. The left rail picks the Block; this
// renders that Block's sections (BLOCK_NAV[block].tabs) as tabs, color-coded to
// the Block accent. Shared routes carry ?block= so the active Block is stable.
// On desktop, Universal's tabs already live in the always-visible UniversalBar,
// so `skipUniversal` suppresses the duplicate row; mobile still needs it here.
export default function BlockTabBar({ skipUniversal = false }) {
  const { t } = useI18n()
  const { theme, block } = useTheme()
  const { unread: messagesUnread } = useMessagesUnread()
  const { pending: janetPending } = useJanetQueueCount()
  const location = useLocation()

  if (skipUniversal && block === 'universal') return null

  const nav = BLOCK_NAV[block] || BLOCK_NAV.universal
  const blk = BLOCKS[block] || BLOCKS.default
  const tabs = nav.tabs || []
  if (tabs.length === 0) return null

  const isDark = theme === 'rothko' || theme === 'chatrone-dark'
  const baseText = isDark ? 'rgba(255,255,255,0.58)' : '#6B6862'
  const activeText = isDark ? '#FFFFFF' : blk.accent
  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)'

  // Longest-match wins: when one tab's path is a prefix of another's (the
  // merged Production block has both /studio and /studio/hub), only the most
  // specific matching tab lights up.
  const matchLen = (path) => {
    const p = path.split('?')[0]
    if (p === '/') return location.pathname === '/' ? 1 : -1
    return (location.pathname === p || location.pathname.startsWith(p + '/')) ? p.length : -1
  }
  const bestLen = Math.max(...tabs.map(t => matchLen(t.path)))
  const tabIsActive = (path) => bestLen >= 0 && matchLen(path) === bestLen

  return (
    <div
      data-shell="block-tabs"
      className="flex flex-wrap justify-center items-stretch gap-x-0.5 gap-y-1 px-3 py-1 shrink-0 border-b md:flex-nowrap md:justify-start md:gap-y-0 md:py-0 md:overflow-x-auto md:scroll-tabs"
      style={{ borderColor }}
    >
      {tabs.map(tab => {
        const active = tabIsActive(tab.path)
        const Icon = TAB_ICONS[tab.path.split('?')[0]]
        const basePath = tab.path.split('?')[0]
        const badgeCount = basePath === '/messages' ? messagesUnread : basePath === '/janet/queue' ? janetPending : 0
        const showUnread = badgeCount > 0
        return (
          <Link
            key={tab.path}
            to={tab.path}
            className="relative inline-flex items-center gap-1 whitespace-nowrap px-2 py-1.5 text-xs font-medium transition-colors hover:opacity-90 md:gap-1.5 md:px-3 md:py-2.5 md:text-sm"
            style={{
              color: active ? activeText : baseText,
              borderBottom: active ? `2px solid ${blk.accent}` : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            {Icon && <Icon size={15} />}
            {t(tab.labelKey)}
            {showUnread && (
              <span className="ml-1 text-[10px] font-bold text-white bg-terra-500 px-1.5 py-0.5 rounded-full min-w-[16px] text-center leading-none">
                {badgeCount > 99 ? '99+' : badgeCount}
              </span>
            )}
          </Link>
        )
      })}
    </div>
  )
}
